import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useTranslate } from 'react-admin';
import { formatCurrency } from '../../utils/formatting';
import SettlementEntryDetailModal from './SettlementEntryDetailModal';
import { formatSettlementEntryLabel, formatSettlementEntryType } from './settlementEntryLabels';

const money = (value) => formatCurrency(Number(value) || 0);

const SummaryRow = ({ label, value, bold }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
    <Typography variant="body1" sx={{ fontWeight: bold ? 700 : 400 }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ fontWeight: bold ? 700 : 400 }}>
      {money(value)}
    </Typography>
  </Box>
);

/**
 * Sale Report Summary: Total Revenue + settlement + payable + actions.
 * Venue / Org / Gişe accordion'ları sayfa gövdesinde (Sales ile aynı sırada).
 */
const OwnershipSalesSummary = ({
  event,
  totals,
  isAdmin,
  onAddExpense,
  onAddIncome,
  onPay,
  onDeleteSettlement,
  paymentStatusSlot,
}) => {
  const translate = useTranslate();
  const settlementEntries = Array.isArray(event?.settlementEntries) ? event.settlementEntries : [];
  const [detailEntry, setDetailEntry] = useState(null);

  const handleDeleteFromRow = async (e, entry) => {
    e.stopPropagation();
    if (!onDeleteSettlement) return;
    if (!window.confirm('Bu düzeltme kaydını silmek istediğine emin misin?')) return;
    await onDeleteSettlement(entry);
  };

  return (
    <Box
      sx={{
        width: '100%',
        fontSize: 16,
        p: 2.5,
        border: '1px solid #e6e6e6',
        borderRadius: 2,
        background: '#ffffff',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          pb: 1.5,
          borderBottom: '1px solid #eee',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {translate('report.total_revenue', { _: 'Total Revenue' })}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {money(totals.totalRevenue)}
        </Typography>
      </Box>

      <SummaryRow
        label={translate('report.gross_sales', { _: 'Brüt satış' })}
        value={totals.grossSales}
      />
      <SummaryRow
        label={translate('report.total_refunded', { _: 'İade' })}
        value={totals.totalRefunded}
      />
      <SummaryRow
        label={translate('report.net_sales', { _: 'Net satış' })}
        value={totals.netSales}
        bold
      />
      {Array.isArray(totals.partialAmountRefundRows) &&
      totals.partialAmountRefundRows.length > 0 ? (
        <Box sx={{ mt: 1, mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {translate('report.partial_amount_refunds', {
              _: 'Tutar bazlı kısmi iadeler (biletler geçerli)',
            })}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>
                  {translate('report.sale', { _: 'Satış' })}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>
                  {translate('report.item_note', { _: 'Not' })}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 13 }}>
                  {translate('report.amount', { _: 'Tutar' })}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {totals.partialAmountRefundRows.map((row) => (
                <TableRow key={`${row.saleId}-${row.at || ''}`}>
                  <TableCell sx={{ fontSize: 13 }}>{row.saleId}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{row.note || '—'}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 13 }}>
                    − {money(row.amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null}

      <Box sx={{ height: 1, background: '#eee', my: 1.5 }} />

      <SummaryRow
        label={translate('report.total_remaining_amount', { _: 'Total Remaining Amount' })}
        value={totals.totalRemainingAmount}
      />
      <SummaryRow
        label={translate('report.total_coupon_discount', { _: 'Total Coupon Discount' })}
        value={totals.totalCouponDiscount}
      />
      <SummaryRow
        label={translate('report.organisator_sales_cash', { _: 'Organisator Sales (Cash)' })}
        value={totals.orgVenueSales}
      />

      <Box sx={{ height: 1, background: '#eee', my: 1.5 }} />

      <SummaryRow
        label={translate('report.sub_total', { _: 'Sub Total' })}
        value={totals.subTotal}
        bold
      />
      <SummaryRow
        label={`${translate('report.gise_fee', { _: 'Gişe Kıbrıs Fee' })} (${event?.isCommissionExtra ? 'extra' : 'included'} ${event?.commissionFee ?? 0}%)`}
        value={totals.totalCommissionAmount}
      />

      {settlementEntries.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            {translate('report.adjustments', { _: 'Düzeltmeler' })}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>
                  {translate('report.type', { _: 'Tip' })}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>
                  {translate('report.item_note', { _: 'Kalem / Not' })}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 14 }}>
                  {translate('report.amount', { _: 'Tutar' })}
                </TableCell>
                {isAdmin ? (
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 14, width: 48 }} />
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {settlementEntries.map((entry) => {
                const docs = Array.isArray(entry.documents) ? entry.documents : [];
                return (
                  <TableRow
                    key={entry.id}
                    hover
                    onClick={() => setDetailEntry(entry)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ fontSize: 15 }}>
                      {formatSettlementEntryType(entry, translate)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 15 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <span>{formatSettlementEntryLabel(entry, translate)}</span>
                        {docs.length > 0 ? (
                          <Tooltip title={`${docs.length} evrak`}>
                            <AttachFileIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          </Tooltip>
                        ) : null}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 15 }}>
                      {entry.type === 'expense' ? '− ' : '+ '}
                      {money(entry.amount)}
                    </TableCell>
                    {isAdmin ? (
                      <TableCell align="right" sx={{ py: 0.5 }}>
                        <IconButton
                          size="small"
                          color="error"
                          aria-label="Sil"
                          onClick={(e) => handleDeleteFromRow(e, entry)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })}
              <TableRow>
                <TableCell colSpan={2} sx={{ fontWeight: 700, fontSize: 15 }}>
                  {translate('report.settlement_net', { _: 'Settlement Net' })}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 15 }}>
                  {money(totals.settlementNet)}
                </TableCell>
                {isAdmin ? <TableCell /> : null}
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      )}

      <Box sx={{ height: 1, background: '#eee', my: 1.5 }} />

      <SummaryRow
        label={translate('report.total_payable', { _: 'Total Payable' })}
        value={totals.totalPayable}
        bold
      />

      {isAdmin && (
        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" color="error" onClick={onAddExpense}>
            {translate('report.add_expense', { _: 'Add Expense' })}
          </Button>
          <Button size="small" variant="outlined" color="success" onClick={onAddIncome}>
            {translate('report.add_income', { _: 'Add Income' })}
          </Button>
          <Button size="small" variant="contained" onClick={onPay}>
            {translate('report.pay', { _: 'Pay' })}
          </Button>
        </Stack>
      )}

      {paymentStatusSlot ? (
        <>
          <Box sx={{ height: 1, background: '#eee', my: 2 }} />
          {paymentStatusSlot}
        </>
      ) : null}

      <SettlementEntryDetailModal
        open={Boolean(detailEntry)}
        onClose={() => setDetailEntry(null)}
        entry={detailEntry}
        isAdmin={isAdmin}
        onDelete={onDeleteSettlement}
      />
    </Box>
  );
};

export default OwnershipSalesSummary;
